import { Box, Button, Paper, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ReviewerPicker } from "components/Submission/ReviewersEditList";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useUpdateSubmission } from "hooks/useSubmissionMutations";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { DateTime } from "luxon";
import { useState } from "react";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { formatDateLikeForDisplay, toUtcMidnightIsoFromDateLike } from "util/dates";

import { StepHeader } from "../StepHeader";

type StaffAssignee = NonNullable<ReturnType<typeof useWasteStepper>["submission"]>["staffAssignee"];

type AssigneeValue = { id: string; name: string; email: string } | null;

function toAssigneeValue(assignee: StaffAssignee): AssigneeValue {
  if (!assignee) return null;
  const name =
    [assignee.person?.first, assignee.person?.last].filter(Boolean).join(" ") || assignee.email;
  return { id: assignee.id, name, email: assignee.email };
}

/**
 * Details stepper step — submission-level metadata (title, due date, Encamp
 * assignee). Staff-only editing; rendered read-only when the user can't edit or
 * the submission is frozen (snapshotted), matching the other read-only sections.
 * Saving persists via `updateSubmission` and marks the step complete.
 *
 * Reviewers/signer/peer-review live on the Review step; the comment stream lives
 * on Verify & Submit.
 */
export function DetailsStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const alerts = useAlerts();
  const { submission, canEdit } = useWasteStepper();
  const [updateSubmission, { loading }] = useUpdateSubmission();
  const { touchStep } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const title = submission?.title ?? "";
  const dueDate = submission?.dueDate ?? null;
  const staffAssignee = submission?.staffAssignee ?? null;

  const [titleValue, setTitleValue] = useState<string>(title);
  const [date, setDate] = useState<DateTime | null>(dueDate ? DateTime.fromISO(dueDate) : null);
  const [assignee, setAssignee] = useState<AssigneeValue>(toAssigneeValue(staffAssignee));

  const handleSave = async () => {
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            title: titleValue.trim() || undefined,
            dueDate: date ? (toUtcMidnightIsoFromDateLike(date) ?? undefined) : undefined,
            staffAssigneeId: assignee?.id ?? null,
          },
        },
      });
      await touchStep(SubmissionStepType.SubmissionDetails, SubmissionStepStatus.Completed);
      alerts.success("Submission details saved.");
      goToNextStep(SubmissionStepType.SubmissionDetails);
    } catch (error) {
      alerts.error("Failed to save submission details.", error);
    }
  };

  // Fields stack full-width within a capped `maxWidth` so the form reads cleanly
  // rather than sitting in a narrow left strip. Title and Encamp Assignee each get
  // their own row; the compact Due date tucks into the action row on the left,
  // sharing it with the right-aligned Save button (which fills otherwise-dead space
  // and anchors the primary action). All three fields use consistent floating
  // outlined labels at `size="small"`.
  const formSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: 760,
  } as const;

  // Read-only (reviewers and frozen submissions): pure text in the same grid as
  // the editable form below, rather than disabled inputs.
  if (!canEdit) {
    return (
      <Stack spacing={2}>
        <StepHeader title="Details" />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={formSx}>
            <DisplayOnlyField label="Title" text={title} />
            <DisplayOnlyField
              label="Encamp Assignee"
              text={toAssigneeValue(staffAssignee)?.name ?? ""}
            />
            <DisplayOnlyField
              label="Due date"
              text={dueDate ? formatDateLikeForDisplay(dueDate) : ""}
            />
          </Box>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <StepHeader title="Details" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={formSx}>
          <TextField
            label="Title"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            fullWidth
            size="small"
          />
          <ReviewerPicker
            staffOnly
            value={assignee ? { ...assignee, order: 0, isStaff: true } : null}
            onChange={(reviewer) =>
              setAssignee(
                reviewer
                  ? {
                      id: reviewer.id,
                      name: reviewer.name,
                      email: reviewer.email,
                    }
                  : null
              )
            }
            inputLabelProps={{ label: "Encamp Assignee", size: "small" }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <DatePicker
              label="Due date"
              value={date}
              onChange={setDate}
              slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
            />
            <Button variant="contained" loading={loading} onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
}
